import { useState, type ReactNode } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Receipt, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { useMyGroups } from "../api/hooks";
import type { GroupListItem } from "../api/types";

/** How many recent groups to surface as quick-tap cards before "All groups". */
const RECENT_LIMIT = 4;

/** Shared styling for the fixed bottom-right "+" button (menu or shortcut). */
const FAB_CLASS =
  "fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-6 z-40 grid size-14 cursor-pointer place-items-center rounded-full bg-primary text-on-primary shadow-xl transition duration-150 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:right-8";

/**
 * Floating "+" action menu. Opens a sheet whose centrepiece is a set of cards
 * for the most recently-used groups: tapping one jumps straight into the New
 * expense screen for that group (where the scan / photo / PDF buttons live).
 * Below the cards are the always-available actions — "New group" and, when the
 * user has more groups than we surface, "All groups" (the dashboard) to reach
 * the rest.
 */
export function FabMenu(): ReactNode {
  const navigate = useNavigate();
  const { data } = useMyGroups();
  const groups = data?.items ?? [];
  const [open, setOpen] = useState(false);

  // Most-recently-updated groups first, so the quick-tap cards are the ones the
  // user is most likely to be adding to.
  const recent = [...groups]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, RECENT_LIMIT);
  const hasMore = groups.length > recent.length;

  // When on a group page, that group is the obvious target: the FAB skips the
  // picker and jumps straight into its new-expense flow. (We only treat it as
  // "current" once it's confirmed to be one of the user's groups.)
  const groupRouteId = useMatch("/groups/:groupId/*")?.params.groupId;
  const currentGroupId =
    groupRouteId && groups.some((g) => g.id === groupRouteId)
      ? groupRouteId
      : undefined;

  function go(to: string): void {
    setOpen(false);
    navigate(to);
  }

  // On a group page the FAB is a direct shortcut straight to that group's
  // new-expense flow — render a plain navigating button, no menu.
  if (currentGroupId) {
    return (
      <button
        type="button"
        aria-label="Add an expense"
        onClick={() => navigate(`/groups/${currentGroupId}/expenses/new`)}
        className={FAB_CLASS}
      >
        <Plus className="size-6" />
      </button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Create"
          // While the menu is open the dialog's translucent overlay covers the
          // page, but this fixed FAB is a portal-free sibling that still shows
          // through it in the bottom-right corner — on narrow (mobile-width)
          // viewports the vertically-centred menu sits right next to it, so the
          // FAB looks like it's covering the menu. Fade it out while open so the
          // menu stands on its own; it fades back in on close.
          className={`${FAB_CLASS} ${
            open ? "pointer-events-none opacity-0" : ""
          }`}
        >
          <Plus className="size-6" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add an expense</DialogTitle>
          <DialogDescription>
            {groups.length > 0
              ? "Pick a group to add an expense — scan a receipt or enter it by hand."
              : "Create your first group to start tracking shared expenses."}
          </DialogDescription>
        </DialogHeader>

        {recent.length > 0 ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {recent.map((g) => (
              <GroupTile
                key={g.id}
                group={g}
                onClick={() => go(`/groups/${g.id}/expenses/new`)}
              />
            ))}
          </div>
        ) : null}

        <div className="mt-5 flex flex-col gap-2">
          <ActionButton
            icon={<Users className="size-5" />}
            label="New group"
            onClick={() => go("/groups/new")}
          />
          {hasMore ? (
            <ActionButton
              icon={<ChevronRight className="size-5" />}
              label="All groups"
              onClick={() => go("/")}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A tappable recent-group card. Mirrors the dashboard GroupCard's identity. */
function GroupTile({
  group,
  onClick,
}: {
  group: GroupListItem;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer items-center gap-3 rounded-lg border border-outline-variant/60 bg-surface-container-low px-4 py-4 text-left transition-colors hover:border-primary/50 hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-primary text-on-primary">
        <Receipt className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-title-md text-on-surface">
          {group.label}
        </span>
        <span className="block text-label-sm text-on-surface-variant">
          {group.currency} · add expense
        </span>
      </span>
    </button>
  );
}

/** A secondary full-width action (New group / All groups). */
function ActionButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-outline-variant/60 bg-surface-container-lowest px-4 py-3 text-label-lg text-on-surface transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="text-primary">{icon}</span>
      {label}
    </button>
  );
}
